<template>
  <div class="dashboard bg-white min-h-screen">
    <!-- Header -->
    <div class="bg-white/90 backdrop-blur-sm shadow-2xl border border-black/20 rounded-lg mx-4 my-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 class="text-3xl font-bold text-black mb-2">Dashboard</h1>
        <p class="text-gray-600">Monitor your application performance and user activity</p>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-black">Total Users</h3>
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center">
              <span class="text-white font-bold text-sm">👥</span>
            </div>
          </div>
          <p class="text-3xl font-bold text-black">1,234</p>
          <p class="text-sm text-gray-600">+12% from last month</p>
        </div>
        
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-black">Active Sessions</h3>
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center">
              <span class="text-white font-bold text-sm">🔥</span>
            </div>
          </div>
          <p class="text-3xl font-bold text-black">456</p>
          <p class="text-sm text-gray-600">+8% from last hour</p>
        </div>
        
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-black">API Calls</h3>
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center">
              <span class="text-white font-bold text-sm">⚡</span>
            </div>
          </div>
          <p class="text-3xl font-bold text-black">12.5K</p>
          <p class="text-sm text-gray-600">+24% from yesterday</p>
        </div>
        
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-black">Response Time</h3>
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center">
              <span class="text-white font-bold text-sm">⚡</span>
            </div>
          </div>
          <p class="text-3xl font-bold text-black">142ms</p>
          <p class="text-sm text-gray-600">-18% improvement</p>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6">
          <h3 class="text-xl font-semibold text-black mb-4">User Activity</h3>
          <div class="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
            <p class="text-gray-500">Chart placeholder</p>
          </div>
        </div>
        
        <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6">
          <h3 class="text-xl font-semibold text-black mb-4">Performance Metrics</h3>
          <div class="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
            <p class="text-gray-500">Chart placeholder</p>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6">
        <h3 class="text-xl font-semibold text-black mb-4">Recent Activity</h3>
        <div class="space-y-4">
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-black/10">
            <div>
              <p class="font-medium text-black">User login</p>
              <p class="text-sm text-gray-600">2 minutes ago</p>
            </div>
            <div class="text-sm text-green-600">Success</div>
          </div>
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-black/10">
            <div>
              <p class="font-medium text-black">API request</p>
              <p class="text-sm text-gray-600">5 minutes ago</p>
            </div>
            <div class="text-sm text-blue-600">Processing</div>
          </div>
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-black/10">
            <div>
              <p class="font-medium text-black">File upload</p>
              <p class="text-sm text-gray-600">10 minutes ago</p>
            </div>
            <div class="text-sm text-green-600">Completed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

// Mock data for demonstration
const stats = ref({
  users: 1234,
  sessions: 456,
  apiCalls: 12500,
  responseTime: 142
})

const activities = ref([
  { id: 1, type: 'User login', time: '2 minutes ago', status: 'Success' },
  { id: 2, type: 'API request', time: '5 minutes ago', status: 'Processing' },
  { id: 3, type: 'File upload', time: '10 minutes ago', status: 'Completed' }
])

onMounted(() => {
  // Simulate real-time updates
  setInterval(() => {
    stats.value.users += Math.floor(Math.random() * 5)
    stats.value.sessions += Math.floor(Math.random() * 3)
    stats.value.apiCalls += Math.floor(Math.random() * 100)
    stats.value.responseTime = Math.max(100, stats.value.responseTime + Math.floor(Math.random() * 20) - 10)
  }, 5000)
})
</script>

<style scoped>
.dashboard {
  /* Custom animations for the aura effect */
}

/* Enhanced hover effects */
.hover-lift {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Smooth transitions */
.transition-all {
  transition: all 0.3s ease;
}

/* Transform effects */
.transform {
  transition: transform 0.3s ease;
}

.transform:hover {
  transform: scale(1.05);
}

/* Chart placeholder styling */
.chart-placeholder {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
}
</style>